import React, { Suspense, useContext, useMemo } from "react";

import { Switch } from "react-router-dom";

import ErrorPage from "@components-shared/mobile/error/ErrorPage";
import { AppLayout } from "@components-shared/mobile/layout";
import { GlobalLoader } from "@components/global-loader/GlobalLoader.mobile";
import ErrorHandler from "@components/layout/ErrorHandler";
import { NoMatchRoute, RouteElement } from "@components/navigation";
import { PopupDisplay, WaitingPeriodSlipSubmitPopup, WorldCupLinkPopup } from "@components/popups/mobile";
import { CapacitorUpdateContainer } from "@components/popups/mobile/CapacitorUpdateContainer";
import { RestrictedPage } from "@components/restricted/mobile";
import { AppContextContainer } from "@containers";
import AppUpdatedPage from "@features/app-updated-page/mobile/AppUpdatedPage";
import { ChatWrap } from "@features/chat/mobile";
import { ConnectionLostPage } from "@features/connection-lost/mobile";
import { LoadingContainer } from "@hoc";
import { useRootAppStore } from "@hooks";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { AppContext } from "@shared/context";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";
import { useAddTipsFromUrl } from "@state/hooks/offer/useAddTipsFromUrl";
import FavoriteStore from "@state/stores/offer/shared/FavoritesStore";
import { THEME_SETTINGS } from "@theme-settings";
import { getCurrentCulture } from "@utils";
import { NotificationContainer } from "@utils/notifications";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import { observer } from "mobx-react";

//#region lazy

const loadFailPath = `/${getCurrentCulture()}/app-update`;

//prettier-ignore
const NewsLayout = lazy(loadFailPath, () => import("@features/cms/news/mobile/NewsLayout"));
// prettier-ignore
const MyBetSlipSharedPage = lazy(loadFailPath, () => import("@features/my-bets/mobile/bet-slip-shared-page/MyBetSlipSharedPage"))
// prettier-ignore
const InfoPagesLayout = lazy(loadFailPath, () => import('./InfoPagesLayout'))
// prettier-ignore
const AccountSettingsLayout = lazy(loadFailPath, () => import("./AccountSettingsLayout"))
// prettier-ignore
const AccountStatementLayout = lazy(loadFailPath, () => import("./AccountStatementLayout"));
// prettier-ignore
const SportsBettingLayout = lazy(loadFailPath, () => import("./SportsBettingLayout"));
// prettier-ignore
const MembershipLayout = lazy(loadFailPath, () => import("./MembershipLayout"));
// prettier-ignore
const EmailActivationPage = lazy(loadFailPath, () => import("@features/membership/mobile/email-activation/EmailActivationPage"));
// prettier-ignore
const LoginPage = lazy(loadFailPath, () => import("@features/membership/mobile/login/LoginPage"));
// prettier-ignore
const MyBetsPageLayout = lazy(loadFailPath, () => import("./MyBetsPageLayout"));
// prettier-ignore
const DepositPage = lazy(loadFailPath, () => import("@features/payment/deposit/mobile/DepositPage"));
// prettier-ignore
const DepositInfoPage = lazy(loadFailPath, () => import("@features/payment/deposit-info/mobile/DepositInfoPage"));
// prettier-ignore
const WithdrawalPage = lazy(loadFailPath, () => import("@features/payment/withdrawal/mobile/WithdrawalPage"));
// prettier-ignore
const BetSlipPage = lazy(loadFailPath, () => import("@components/bet-slip/mobile/BetSlipPage"));
// prettier-ignore
const MiniBetSlip = lazy(loadFailPath, () => import("@components/bet-slip/mobile/MiniBetSlip"));
// prettier-ignore
const BetSlipReceiptPage = lazy(loadFailPath, () => import("@features/my-bets/mobile/bet-slip-receipt/BetSlipReceiptPage"));
// prettier-ignore
const ResultsPage = lazy(loadFailPath, () => import("@features/results/mobile/ResultsPage"));
// prettier-ignore
const GamingHistoryPage = lazy(loadFailPath, () => import("@features/games/gaming-history/mobile/GamingHistoryPage"));
// prettier-ignore
const PaymentInfoPage = lazy(loadFailPath, () => import("@features/payment/payment-info/mobile/PaymentInfoPage"));
// prettier-ignore
const QRCodeScanner = lazy(loadFailPath, () => import("@components/qr-code-scanner/mobile/QRCodeScannerContainer"));
// prettier-ignore
const RegistrationActivationPage = lazy(loadFailPath, () => import("@features/membership/mobile/registration-activation/RegistrationActivationPage"));
// prettier-ignore
const OnboardingRegistrationPage = lazy(loadFailPath, () => import("../membership/onboarding-registration/OnboardingRegistrationPage"));
// prettier-ignore
const BetSlipDetailsPage = lazy(loadFailPath, () => import("@features/my-bets/mobile/qr-scanned-details/BetSlipDetailsPage"));
// prettier-ignore
const UserMenu = lazy(loadFailPath, () => import("@components-shared/mobile/user-menu/UserMenu"));
// prettier-ignore
const AccountRecheckPage = lazy(loadFailPath, () => import("@features/account-recheck/mobile/AccountRecheckPage"));
// prettier-ignore
const LimitIncreasePage = lazy(loadFailPath, () => import("@features/limit-increase/mobile/LimitIncreasePage"));
// prettier-ignore
const QuickPlayPageLayout = lazy(loadFailPath, () => import("@features/quick-play/mobile/layout/QuickPlayPageLayout"));
// prettier-ignore
const EventSummaryPage = lazy(loadFailPath, () => import("@features/notifications/event-summary/mobile/EventSummaryPage"));
//prettier-ignore
const UserDataConfirmAuthPage = lazy(loadFailPath, () => import("@features/membership/user-data-confirm/mobile/UserDataConfirmAuthPage"));
//prettier-ignore
const PlayerSowPage = lazy(loadFailPath, () =>  import("@features/player-sow/mobile"));
// prettier-ignore
const GamingRisksPage = lazy(loadFailPath, () => import("@features/gaming-risks"));
// prettier-ignore
const ChatSupportPage = lazy(loadFailPath, () => import("@features/chat/mobile/support-chat/ChatSupportPage"));
// prettier-ignore
const PartnerRegistrationPage = lazy(loadFailPath, () => import("../membership/partner-registration/PartnerRegistrationPage"));
// prettier-ignore
const FreebetEmailConfirmation = lazy(loadFailPath, () => import("@features/freebet/mobile/FreebetEmailConfirmation"));

//prettier-ignore
const ScorePredictorLayout = lazy(loadFailPath, () => import("@features/score-predictor/mobile/ScorePredictorLayout"));
//#endregion lazy

export default observer(function MainLayoutWrapper() {
	return (
		<Suspense fallback={<GlobalLoader />}>
			<AppContextContainer>
				<NotificationContainer>
					<AppLayout>
						<ChatWrap>
							<MainLayout />
							<CapacitorUpdateContainer />
						</ChatWrap>
					</AppLayout>
				</NotificationContainer>
			</AppContextContainer>
		</Suspense>
	);
});

const MainLayout = observer(function MainLayout() {
	const { isNavMenuOpen, setIsNavMenuOpen } = useContext(AppContext);
	const rootStore = useRootAppStore();
	const favoriteStore = useMemo(() => new FavoriteStore(rootStore), []);

	useAddTipsFromUrl();

	return (
		<ErrorPage
			isNavMenuOpen={isNavMenuOpen}
			setIsNavMenuOpen={setIsNavMenuOpen}
			full
		>
			<LoadingContainer>
				<WaitingPeriodSlipSubmitPopup />
				<PopupDisplay />
				<WorldCupLinkPopup />
				<ErrorHandler />
				<Switch>
					<RouteElement exact path="/:culture/app-update">
						<AppUpdatedPage
							isNavMenuOpen={isNavMenuOpen}
							setIsNavMenuOpen={setIsNavMenuOpen}
							reload={() => {
								window.location.reload();
							}}
						/>
					</RouteElement>

					<RouteElement exact path="/:culture/bet-slip">
						<BetSlipPage />
					</RouteElement>

					<RouteElement
						path={[
							"/:culture/home/",
							"/:culture/sports/",
							"/:culture/live/",
							"/:culture/search/",
						]}
					>
						<FavoritesStoreContext.Provider value={favoriteStore}>
							<SportsBettingLayout />
						</FavoritesStoreContext.Provider>
						<MiniBetSlip />
					</RouteElement>

					{isCapacitorPlatform() && (
						<RouteElement path={["/:culture/event-summary"]}>
							<EventSummaryPage />
						</RouteElement>
					)}

					<RouteElement path={["/:culture/info"]}>
						<InfoPagesLayout />
					</RouteElement>

					<RouteElement path={["/:culture/news-announcements"]}>
						<NewsLayout />
					</RouteElement>

					<RouteElement path={["/:culture/results"]}>
						<ResultsPage />
					</RouteElement>

					<RouteElement
						privateRoute
						path={["/:culture/account-settings/"]}
					>
						<AccountSettingsLayout
							setIsNavMenuOpen={setIsNavMenuOpen}
						/>
					</RouteElement>

					<RouteElement exact path="/:culture/auth/login">
						<LoginPage />
					</RouteElement>

					{WEBPACK_IS_REGISTRATION_ENABLED && (
						<RouteElement exact path="/:culture/auth/register">
							<OnboardingRegistrationPage />
						</RouteElement>
					)}

					<RouteElement exact path="/:culture/auth/register/partner">
						<PartnerRegistrationPage />
					</RouteElement>

					{WEBPACK_IS_REGISTRATION_ENABLED && (
						<RouteElement
							exact
							path="/:culture/auth/register-activation"
						>
							<RegistrationActivationPage />
						</RouteElement>
					)}

					<RouteElement path={["/:culture/auth"]}>
						<MembershipLayout />
					</RouteElement>

					<RouteElement
						path="/:culture/account-statement"
						privateRoute
					>
						<AccountStatementLayout />
					</RouteElement>

					<RouteElement path="/:culture/my-bets" privateRoute>
						<MyBetsPageLayout />
					</RouteElement>

					{WEBPACK_IS_CASINO_ENABLED ||
					WEBPACK_IS_CASINO_LIVE_ENABLED ? (
						<RouteElement
							path="/:culture/gaming-history"
							privateRoute
						>
							<GamingHistoryPage />
						</RouteElement>
					) : null}

					<RouteElement path="/:culture/deposit" privateRoute>
						<DepositPage />
					</RouteElement>

					<RouteElement path="/:culture/deposit-info" privateRoute>
						<DepositInfoPage />
					</RouteElement>

					<RouteElement path="/:culture/withdrawal" privateRoute>
						<WithdrawalPage />
					</RouteElement>

					<RouteElement path="/:culture/payment-info/:status">
						<PaymentInfoPage />
					</RouteElement>

					<RouteElement path="/:culture/email-activation">
						<EmailActivationPage />
					</RouteElement>

					<RouteElement
						path="/:culture/user-profile-recheck"
						privateRoute
					>
						<AccountRecheckPage />
					</RouteElement>

					<RouteElement path="/:culture/limit-increase" privateRoute>
						<LimitIncreasePage />
					</RouteElement>

					<RouteElement path="/:culture/quick-play">
						<QuickPlayPageLayout />
						<MiniBetSlip />
					</RouteElement>

					<RouteElement
						exact
						path={["/:culture/sow/document-upload"]}
					>
						<PlayerSowPage />
					</RouteElement>

					<RouteElement path="/:culture/gaming-risks">
						<GamingRisksPage />
					</RouteElement>

					{THEME_SETTINGS.showBetQRScanner ? (
						<RouteElement exact path="/:culture/check-bet-slip">
							<QRCodeScanner />
						</RouteElement>
					) : null}

					{THEME_SETTINGS.showBetQRScanner ? (
						<RouteElement
							exact
							path="/:culture/check-bet-slip/:encryptedBetSlipNumber"
						>
							<BetSlipDetailsPage />
						</RouteElement>
					) : null}

					<RouteElement
						path="/:culture/bet-slip/bet-slip-receipt/:betId/:walletId"
						privateRoute
					>
						<BetSlipReceiptPage />
					</RouteElement>

					<RouteElement path="/:culture/restrict">
						<RestrictedPage />
					</RouteElement>

					<RouteElement path="/:culture/bet-slip-shared/:betSlipNumber">
						<MyBetSlipSharedPage />
					</RouteElement>

					{WEBPACK_IS_SUPPORT_CHAT_AVAILABLE ? (
						<RouteElement path="/:culture/support-chat">
							<ChatSupportPage />
						</RouteElement>
					) : null}

					<RouteElement path="/:culture/offline">
						<ConnectionLostPage
							isNavMenuOpen={isNavMenuOpen}
							setIsNavMenuOpen={setIsNavMenuOpen}
							full
						/>
					</RouteElement>

					<RouteElement path="/:culture/user-data-confirmation">
						<UserDataConfirmAuthPage />
					</RouteElement>

                    <RouteElement path={["/:culture/freebet/:action/:freebetId"]}>
						<FreebetEmailConfirmation />
					</RouteElement>

                    <RouteElement path={["/:culture/score-predictor"]}>
                        <ScorePredictorLayout />
                    </RouteElement>

					<NoMatchRoute />
				</Switch>
				{isNavMenuOpen && (
					<UserMenu
						isNavMenuOpen
						setIsNavMenuOpen={setIsNavMenuOpen}
					/>
				)}
			</LoadingContainer>
		</ErrorPage>
	);
});
